import { pageSeo } from "../utils/seoContent";
import { FacebookOutlined, InstagramOutlined, MessageOutlined, WhatsAppOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { BrandJsonLd, LocalBusinessJsonLd, LogoJsonLd, NextSeo } from "next-seo";
import { motion } from "framer-motion";
import Head from "next/head";
import Link from "next/link";

const ContactUs = () => {
    const contactMethods = [
        {
            icon: <WhatsAppOutlined />,
            title: "WhatsApp",
            description: "Quick responses for urgent inquiries",
            link: "whatsapp://api.whatsapp.com/send?phone=94776411718",
            value: "+94 77 641 17 18",
            color: "#25D366"
        },
        {
            icon: <InstagramOutlined />,
            title: "Instagram",
            description: "Follow our latest collections",
            link: "https://www.instagram.com/chethi.s_",
            value: "@chethi.s_",
            color: "#E4405F"
        },
        {
            icon: <FacebookOutlined />,
            title: "Facebook",
            description: "Connect with our community",
            link: "https://facebook.com/chethi.fashion",
            value: "Chethi's",
            color: "#1877F2"
        },
        {
            icon: <MessageOutlined />,
            title: "Messenger",
            description: "Direct messaging support",
            link: "https://m.me/105313118981308",
            value: "Chethi's",
            color: "#0084FF"
        }
    ];

    return (
        <>
            <Head>
                <title>Contact Us - Chethi&apos;s</title>
            </Head>
            <NextSeo {...pageSeo.contact} />
            <LogoJsonLd {...pageSeo.logoJsonLD} />
            <LocalBusinessJsonLd {...pageSeo.localBusinessJsonLD} />
            <BrandJsonLd {...pageSeo.brandJsonLD} />
            
            <div className="luxury-contact">
                {/* Hero Section */}
                <motion.div 
                    className="luxury-contact__hero"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="luxury-contact__hero-content">
                        <motion.h1 
                            className="luxury-contact__title"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Get in Touch
                        </motion.h1>
                        <motion.p 
                            className="luxury-contact__subtitle"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            We&apos;d love to hear from you. Reach out for personalized styling advice, 
                            custom orders, or any questions about our sustainable luxury fashion.
                        </motion.p>
                    </div>
                </motion.div>

                <div className="luxury-contact__container">
                    {/* Contact Methods Grid */}
                    <motion.div 
                        className="luxury-contact__methods"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                    >
                        {contactMethods.map((method, index) => (
                            <motion.div
                                key={method.title}
                                className="luxury-contact__method"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                                whileHover={{ y: -8, scale: 1.02 }}
                            >
                                <Link href={method.link} target="_blank" rel="noopener noreferrer">
                                    <div className="luxury-contact__method-icon" style={{ color: method.color }}>
                                        {method.icon}
                                    </div>
                                    <h3 className="luxury-contact__method-title">{method.title}</h3>
                                    <p className="luxury-contact__method-description">{method.description}</p>
                                    <div className="luxury-contact__method-value">{method.value}</div>
                                </Link>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Additional Info Section */}
                    <motion.div 
                        className="luxury-contact__info"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.2 }}
                    >
                        <div className="luxury-contact__info-card">
                            <h2 className="luxury-contact__info-title">Why Choose Chethi&apos;s?</h2>
                            <div className="luxury-contact__features">
                                <div className="luxury-contact__feature">
                                    <EnvironmentOutlined className="luxury-contact__feature-icon" />
                                    <div>
                                        <h4>Sustainable Fashion</h4>
                                        <p>Eco-friendly materials and ethical practices</p>
                                    </div>
                                </div>
                                <div className="luxury-contact__feature">
                                    <MailOutlined className="luxury-contact__feature-icon" />
                                    <div>
                                        <h4>Custom Designs</h4>
                                        <p>Personalized pieces tailored to your style</p>
                                    </div>
                                </div>
                                <div className="luxury-contact__feature">
                                    <PhoneOutlined className="luxury-contact__feature-icon" />
                                    <div>
                                        <h4>Expert Support</h4>
                                        <p>Dedicated styling advice and customer care</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div 
                        className="luxury-contact__cta"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.4 }}
                    >
                        <h3 className="luxury-contact__cta-title">Ready to Create Something Beautiful?</h3>
                        <p className="luxury-contact__cta-text">
                            Our team is here to bring your fashion vision to life. 
                            Let&apos;s create something extraordinary together.
                        </p>
                        <Link href="whatsapp://api.whatsapp.com/send?phone=94776411718" className="luxury-contact__cta-button">
                            Start a Conversation
                        </Link>
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default ContactUs;
